/**
 * Read a chosen file as text.
 *
 * `Blob.prototype.text()` is the modern path and every browser this app
 * supports has it, so it is tried first. The `FileReader` fallback is not
 * defensive padding: jsdom's `File` implements NEITHER `text()` nor
 * `arrayBuffer()`, so without it the seed upload could not be tested at all —
 * and a screen whose only untestable step is the one that writes to the
 * connector catalog is the wrong screen to leave untested.
 *
 * It rejects rather than resolving to an empty string when the read fails, so
 * "the file was empty" and "the file could not be read" stay distinguishable;
 * the caller shows different sentences for the two.
 */
export function readTextFile(file: File): Promise<string> {
  if (typeof file.text === "function") return file.text();

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error("The file could not be read."));
    reader.onload = () =>
      typeof reader.result === "string"
        ? resolve(reader.result)
        : reject(new Error("The file could not be read as text."));
    reader.readAsText(file);
  });
}
